import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { MODELS, MODEL_KEYS, ModelChip, ModelBadge } from "../components/ModelBadge";
import Markdown from "../components/Markdown";
import { FileText, Upload, Trash2, Send } from "lucide-react";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [model, setModel] = useState("deepseek");
  const [q, setQ] = useState("");
  const [conversation, setConversation] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/documents");
    setDocs(data.documents || []);
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setActive(data);
      setConversation([]);
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    setDocs((d) => d.filter((x) => x.document_id !== id));
    if (active?.document_id === id) { setActive(null); setConversation([]); }
  };

  const ask = async () => {
    if (!q.trim() || !active || busy) return;
    const question = q;
    setQ("");
    setConversation((c) => [...c, { role: "user", content: question }]);
    setBusy(true);
    try {
      const { data } = await api.post("/documents/analyze", {
        document_id: active.document_id,
        question,
        model,
      });
      const r = data.response;
      setConversation((c) => [...c, { role: "assistant", content: r.content || `⚠️ ${r.error}`, model, error: !!r.error }]);
    } catch (e) {
      setConversation((c) => [...c, { role: "assistant", content: `⚠️ ${e.response?.data?.detail || e.message}`, model, error: true }]);
    } finally { setBusy(false); }
  };

  const m = MODELS[model];

  return (
    <div className="flex h-screen" data-testid="documents-page">
      {/* Left pane */}
      <aside className="w-80 border-r-2 border-slate-900 bg-white p-4 flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={22} strokeWidth={2.5} />
          <h2 className="font-heading text-xl font-bold">Documents</h2>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.txt,.md,.csv,.json,.js,.py,.html"
          className="hidden"
          data-testid="doc-file-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          data-testid="doc-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="brut-btn brut-btn-outline w-full mb-4"
        >
          <Upload size={16} strokeWidth={2.5} /> {uploading ? "Uploading…" : "Upload file"}
        </button>

        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Your files</div>
        <div className="flex-1 overflow-y-auto space-y-2" data-testid="docs-list">
          {docs.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-3">No documents uploaded</div>
          ) : (
            docs.map((d) => (
              <div
                key={d.document_id}
                onClick={() => { setActive(d); setConversation([]); }}
                data-testid={`doc-${d.document_id}`}
                className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${active?.document_id === d.document_id ? "border-slate-900 bg-amber-100 shadow-brut-sm" : "border-slate-300 hover:border-slate-900 bg-white"}`}
              >
                <div className="flex justify-between items-start gap-1">
                  <div className="font-semibold text-sm truncate flex-1">{d.filename}</div>
                  <button onClick={(e) => { e.stopPropagation(); del(d.document_id); }} className="text-slate-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{(d.char_count / 1000).toFixed(1)}k chars</div>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Right pane: Q&A */}
      <div className="flex-1 flex flex-col">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-center px-6">
            <div>
              <div className="text-6xl mb-3 float-y inline-block">📄</div>
              <h2 className="font-heading text-2xl font-bold mb-2">Upload a document to start</h2>
              <p className="text-slate-500 max-w-md">PDF, text, code, markdown — upload a file and ask the models anything about it.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="px-6 py-4 border-b-2 border-slate-900 bg-white">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase font-bold tracking-widest text-slate-500">Analyzing</div>
                  <div className="font-heading text-xl font-bold">{active.filename}</div>
                </div>
                <div className="flex gap-1.5" data-testid="doc-model-selector">
                  {MODEL_KEYS.map((k) => (
                    <ModelChip key={k} id={k} selected={model === k} onClick={() => setModel(k)} />
                  ))}
                </div>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-3xl mx-auto space-y-4">
                {conversation.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Sparkles inline /> Ask anything about this document.
                    <div className="mt-3 flex flex-wrap gap-2 justify-center">
                      {["Summarize this document", "What are the key points?", "Find any mention of dates"].map((p, i) => (
                        <button
                          key={i}
                          data-testid={`doc-suggest-${i}`}
                          onClick={() => setQ(p)}
                          className="px-3 py-1.5 rounded-full bg-white border-2 border-slate-300 hover:border-slate-900 text-sm font-semibold"
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {conversation.map((msg, i) => (
                  msg.role === "user" ? (
                    <div key={i} className="flex justify-end" data-testid={`doc-q-${i}`}>
                      <div className="max-w-[80%] bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-3">{msg.content}</div>
                    </div>
                  ) : (
                    <div key={i} className="flex justify-start" data-testid={`doc-a-${i}`}>
                      <div className="max-w-[85%] border-2 rounded-2xl rounded-bl-sm px-4 py-3" style={{ backgroundColor: m.bg, borderColor: m.border }}>
                        <Markdown text={msg.content} />
                      </div>
                    </div>
                  )
                ))}
                {busy && (
                  <div className="flex justify-start">
                    <div className="border-2 rounded-2xl px-4 py-3 flex gap-1.5" style={{ backgroundColor: m.bg, borderColor: m.border }}>
                      <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border }} />
                      <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.2s" }} />
                      <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.4s" }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <footer className="border-t-2 border-slate-900 bg-white p-4">
              <div className="max-w-3xl mx-auto flex items-end gap-3">
                <textarea
                  data-testid="doc-question-input"
                  rows={1}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                  placeholder="Ask about this document…"
                  className="brut-input flex-1 resize-none max-h-32"
                  style={{ minHeight: 48 }}
                />
                <button
                  data-testid="doc-ask-btn"
                  onClick={ask}
                  disabled={!q.trim() || busy}
                  className="brut-btn"
                  style={{ backgroundColor: m.color, borderColor: m.border }}
                >
                  <Send size={16} strokeWidth={2.5} /> Ask
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function Sparkles({ inline }) {
  return <span className={inline ? "inline-block mr-1" : ""}>✨</span>;
}
